"use client";

// react
import { useMemo, useState } from "react";

// anivive
import { RadioList } from "@anivive/ui";

// services
import { Pet } from "@/services/api/server/pets";

// private
import Buttons from "../../components/buttons";
import PetInfo from "../../components/pet-info";
import { useWizard } from "../../context";
import styles from "../index.module.scss";

export default function StepTwo({ pets = [] }: { pets: Pet[] }) {
  const { trial, actions } = useWizard();

  actions.goBackCheck(2);

  const [_state, setState] = useState({
    petId: pets.length === 0 ? "new" : "",
    mode: pets.length === 0 ? "new-pet" : "",
  });

  const handleOnChange = (value: string) => {
    setState((old) => ({
      ...old,
      petId: value,
    }));

    const selectedPet = pets.find(({ id }) => id === String(value));

    if (selectedPet) {
      return actions.setData({
        pet: {
          id: selectedPet.id,
          practices: selectedPet.practices.map(({ id, name }) => ({ id, name })),
        },
      });
    }
  };

  const handleReset = () => {
    if (pets.length > 0) {
      setState({
        petId: "",
        mode: "",
      });

      return actions.setData({
        pet: undefined,
      });
    }

    actions.prev();
  };

  const options = useMemo(
    () => [
      ...pets.map((p) => {
        const disabled =
          p.species.id !== trial.species.id ||
          (trial.breeds.length > 0 &&
            !trial.breeds.some((breed) => p.breeds.some((b) => b.id === breed.id)));

        return {
          value: p.id,
          label: `${p.name}${disabled ? " (Not Eligible)" : ""}`,
          disabled,
        };
      }),
      { value: "new", label: "None of the above. Add a new pet" },
    ],
    [pets, trial.breeds, trial.species.id],
  );

  if (_state.mode === "new-pet") {
    return (
      <>
        <p className="text-style-body2-regular mt-2">
          Please enter your pet&apos;s information and proceed to the next step so we can build your
          profile.
        </p>
        <div className={styles.container}>
          <PetInfo pets={pets} onCancel={handleReset} />
        </div>
      </>
    );
  }

  return (
    <>
      <p className="text-style-body2-regular mt-2">
        Please select an existing pet to apply or add a new one.
      </p>

      <div className={styles.container}>
        <RadioList
          options={options}
          className="mb-4"
          name="id"
          onChange={handleOnChange}
          value={_state.petId}
        />

        <Buttons
          disabled={!_state.petId}
          onNext={() => {
            if (_state.petId === "new") {
              return setState((old) => ({
                ...old,
                mode: "new-pet",
              }));
            }

            actions.next();
          }}
        />
      </div>
    </>
  );
}
